import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import referralApi from '../../services/referralApi';
import { fetchReferralMe, isReferralAvailable } from '../../store/slices/referralSlice';
import {
  PageHeader,
  SegmentedToggle,
  EmptyState,
  TWButton,
} from '../../components/ui';
import ReferralHero from './components/ReferralHero';
import ReferralStats from './components/ReferralStats';
import ShareModal from './components/ShareModal';
import ReferralHistoryTable from './components/ReferralHistoryTable';
import ReferralLedgerTable from './components/ReferralLedgerTable';
import ReferralSkeleton from './components/ReferralSkeleton';
import './referral.css';

const HISTORY_PAGE_SIZE = 10;

const SHELL_CLASS =
  'referral-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

const Referral = () => {
  const dispatch = useDispatch();
  const {
    data: meData,
    loading: meLoading,
    error: meError,
  } = useSelector((state) => state.referralReducer);

  // The company selected in the company changer (the same source the Finance
  // pages read). The API serves the user's first company unless asked for one.
  const companyId = useSelector((state) => state.companyReducer.selected?.id);
  // History and Ledger are local state: an answer for a company the user has
  // already switched away from must not land in the new company's tables.
  const companyRef = useRef(companyId);
  companyRef.current = companyId;

  const [loading, setLoading] = useState({ history: true, ledger: true });
  const [activeTab, setActiveTab] = useState('history');

  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyPagination, setHistoryPagination] = useState({
    pageNumber: 1,
    pageSize: HISTORY_PAGE_SIZE,
    totalRecords: 0,
  });

  const [ledger, setLedger] = useState([]);

  // A company switch (the legacy list changes the selected company in place)
  // starts a new page: the status filter goes back to all, and the previous
  // company's rows and counts go at once — in this render, not after an
  // effect — so they are never shown dimmed under the new company's spinner.
  // React's own "adjust state while rendering" idiom; the effect below
  // fetches the new company's pages.
  const [shownFor, setShownFor] = useState(companyId);
  if (shownFor !== companyId) {
    setShownFor(companyId);
    setHistory([]);
    setHistoryStatus('');
    setHistoryPagination({ pageNumber: 1, pageSize: HISTORY_PAGE_SIZE, totalRecords: 0 });
    setLedger([]);
    setLoading({ history: true, ledger: true });
  }
  const [shareModal, setShareModal] = useState({ open: false, channel: 'sms' });

  const fetchHistory = useCallback(
    async (pageNumber, status) => {
      setLoading((prev) => ({ ...prev, history: true }));
      try {
        const statusPart = status !== '' ? `&status=${status}` : '';
        const query = `?pageNumber=${pageNumber}&pageSize=${HISTORY_PAGE_SIZE}${statusPart}`;
        const res = await referralApi.history(query, companyId);
        if (companyRef.current !== companyId) return;
        setHistory(res.data?.data || []);
        setHistoryPagination({
          pageNumber,
          pageSize: HISTORY_PAGE_SIZE,
          totalRecords: res.data?.totalRecords || 0,
        });
      } catch {
        if (companyRef.current !== companyId) return;
        setHistory([]);
        message.error('Failed to load referral history');
      } finally {
        // The new company's own request owns the loading flag now.
        if (companyRef.current === companyId) {
          setLoading((prev) => ({ ...prev, history: false }));
        }
      }
    },
    [companyId],
  );

  const fetchLedger = useCallback(async () => {
    setLoading((prev) => ({ ...prev, ledger: true }));
    try {
      const res = await referralApi.ledger(companyId);
      if (companyRef.current !== companyId) return;
      setLedger(res.data?.data || []);
    } catch {
      if (companyRef.current !== companyId) return;
      setLedger([]);
      message.error('Failed to load credit ledger');
    } finally {
      if (companyRef.current === companyId) {
        setLoading((prev) => ({ ...prev, ledger: false }));
      }
    }
  }, [companyId]);

  // /Me redux slice'tan gelir (layout ile paylaşılır -> dedup).
  // History + Ledger sayfaya özel, lokal kalır. Seçili şirket değişince üçü de
  // yeniden çekilir (fetchHistory / fetchLedger companyId'ye bağlı).
  useEffect(() => {
    dispatch(fetchReferralMe({ companyId }));
    fetchHistory(1, '');
    fetchLedger();
  }, [dispatch, companyId, fetchHistory, fetchLedger]);

  const handleStatusChange = (value) => {
    setHistoryStatus(value);
    fetchHistory(1, value);
  };

  const handleHistoryPageChange = (page) => {
    fetchHistory(page, historyStatus);
  };

  const handleShare = (channel) => setShareModal({ open: true, channel });
  const closeShare = () => setShareModal((prev) => ({ ...prev, open: false }));

  // İlk paint: /Me çözülene kadar tam sayfa skeleton.
  if (meLoading && !meData) {
    return <ReferralSkeleton />;
  }

  // /Me başarısız -> hero/stat render edilemez; retry sun.
  if (meError && !meData) {
    return (
      <div className={SHELL_CLASS}>
        <div className="mx-auto max-w-5xl">
          <EmptyState
            title="Couldn't load referral data"
            description="Something went wrong while loading your referral dashboard."
            action={
              <TWButton
                variant="primary"
                label="Try again"
                onClick={() => dispatch(fetchReferralMe({ companyId, force: true }))}
              />
            }
          />
        </div>
      </div>
    );
  }

  // Program bu şirket için kapalı ya da ödül 0 veya daha az (menü zaten gizli
  // — doğrudan erişim savunması). Sidebar ile aynı kural.
  if (meData && !isReferralAvailable(meData)) {
    return (
      <div className={SHELL_CLASS}>
        <div className="mx-auto max-w-5xl">
          <EmptyState
            title="Referral program is not available"
            description="The referral program is not available for your account right now."
          />
        </div>
      </div>
    );
  }

  // Rozet, History LİSTESİNİN gerçek sayısını gösterir (davet ettiğim
  // restoranlar). /Me'nin activecount+pendingcount'u beneficiary reward'ları
  // sayar (davet edilince kazanılan dahil) ve listeyle uyuşmaz — onu kullanmayız.
  const historyLabel = (
    <span className="inline-flex items-center gap-1.5">
      History
      {historyPagination.totalRecords > 0 ? (
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[0.65rem] font-bold tabular-nums text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {historyPagination.totalRecords}
        </span>
      ) : null}
    </span>
  );

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeader
          title="Refer & Earn"
          description="Invite other restaurants to Thisway and earn credit on your platform fees."
        />

        <ReferralHero data={meData} onShare={handleShare} />

        <ReferralStats data={meData} />

        <SegmentedToggle
          ariaLabel="Referral sections"
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'history', label: historyLabel },
            { value: 'ledger', label: 'Ledger' },
          ]}
        />

        {activeTab === 'history' ? (
          <ReferralHistoryTable
            data={history}
            loading={loading.history}
            pagination={historyPagination}
            onPageChange={handleHistoryPageChange}
            status={historyStatus}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <ReferralLedgerTable data={ledger} loading={loading.ledger} />
        )}

        <ShareModal
          open={shareModal.open}
          channel={shareModal.channel}
          onClose={closeShare}
        />
      </div>
    </div>
  );
};

export default Referral;
